import { useState } from "react";

/**
 * Detects if the current environment is a WebView (iOS WKWebView, Android WebView, etc.)
 * This can be used to conditionally hide UI elements that would be redundant in a WebView context.
 */
const isWebView = (): boolean => {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (window.isWebView) {
    return true;
  }

  // Android WebView detection
  if (/wv/.test(userAgent)) {
    return true;
  }

  // React Native WebView (common patterns)
  if (/react-native-webview/.test(userAgent)) {
    return true;
  }

  return false;
};

/**
 * Detects if the app is running in an iframe
 */
const isInIframe = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top, we're likely in an iframe
    return true;
  }
};

/**
 * Detects if the app is running in the okthx Electron app
 * based on user agent starting with "okthx"
 */
const isOkthxApp = (): boolean => {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent;
  return userAgent.toLowerCase().startsWith("okthx");
};

/**
 * Detects if the app is running in a WebView or iframe
 */
const isEmbedded = (): boolean => {
  return isWebView() || isInIframe() || isOkthxApp();
};

/**
 * Hook to detect if the app is running in a WebView, iframe, or okthx Electron app
 * Useful for conditionally hiding UI elements that would be redundant in embedded contexts
 */
export const useEmbedded = () => {
  const [isWebViewState] = useState(() => isWebView());
  const [isInIframeState] = useState(() => isInIframe());
  const [isOkthxAppState] = useState(() => isOkthxApp());
  const [isEmbeddedState] = useState(() => isEmbedded());

  return {
    isEmbedded: isEmbeddedState,
    isWebView: isWebViewState,
    isInIframe: isInIframeState,
    isOkthxApp: isOkthxAppState,
  };
};
